import { Metamodel } from '../base/model';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { ModelConstants } from '../model-constants';
import { IViolation } from '../base/annotation';
import { ActionConstants } from '../../constants/action-constants';
import { IRelationship, IColumnRelationshipInformation } from './interface';
import { SharedModel } from '../sharedmodel';
import { DatabaseEntity } from '../database/databaseEntity';
import { TreeNamedResAndNameUtils } from '../../shared/utils/tree-named-naming.util';
import { Table } from '../table/table';
import ShellAPI from 'a365-shell-api';
import { TableUtils } from '../table/table.util';
import { RelationshipUtils } from './relationship.utils';
import { sanitizeAndFormatString } from '../../shared/utils/string.util';

@Metamodel.model({
    name: ModelConstants.relationship,
    display: [shellStringResources.SynapseRelationships],
    reusable: true,
    referable: true,
    validator: Relationship.validate,
    constraints: {
        realName: {
            required: true,
            uniqueModelName: true
        }
    },
    accessPublishConstraint: {
        writeResource: {
            action: ActionConstants.datasetsWrite
        },
        deleteResource: {
            action: ActionConstants.datasetsDelete
        }
    }
})
export class Relationship extends DatabaseEntity {
    public readonly excludedInArmTemplate = true;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Parent
    })
    public parentInfo: SharedModel.IParentInfo;

    @Metamodel.attribute({
        display: shellStringResources.Name
    })
    public name: string; // used in named identity

    @Metamodel.attribute({
        display: shellStringResources.Name
    })
    get realName(): string { // used in name validation
        let name = '';
        if (this.content && this.content.name) {
            name = this.content.name;
        } else {
            const nameDict = TreeNamedResAndNameUtils.extractNameAndParentName(this.name);
            name = nameDict.name;
        }
        return name;
    }

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Description
    })
    public description?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Content
    })
    public content?: IRelationship;

    constructor(name: string, description: string, parentInfo: SharedModel.IParentInfo, content: IRelationship) {
        super(name, description, parentInfo);
        this.content = content;
        this.resourceType = ModelConstants.relationship + 's';
    }

    // todo: this validate breaks multi-context, need to have it fixed before we supporting mul-ctx synpase
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static async validate(owner: any): Promise<IViolation[] | undefined> {
        const named = (owner && owner.named) as Relationship;
        const relationship = named.content;
        if (relationship) {
            if (relationship.newValidation) {
                if (relationship.violations) {
                    relationship.violations.forEach((violation: IViolation) => {
                        violation.context = owner;
                    });
                }
                return relationship.violations;
            }

            const violations: IViolation[] = [];
            const allTables = (await ShellAPI.artifacts.listArtifacts<Table>('table')).filter((artifact) =>
                artifact.parentInfo.resourceName === named.parentInfo.resourceName);
            let fromTable: Table;
            let toTable: Table;
            let valid = true;

            if (relationship.fromTableName === '') {
                violations.push({
                    context: owner,
                    message: () => sanitizeAndFormatString(shellStringResources.Validator_Relationship_TableNameRequired, relationship.toTableName)
                });
                valid = false;
            } else {
                fromTable = allTables.find((artifact) => artifact.realName === relationship.fromTableName);
                valid = fromTable && valid;
            }

            if (relationship.toTableName === '') {
                violations.push({
                    context: owner,
                    message: () => sanitizeAndFormatString(shellStringResources.Validator_Relationship_TableNameRequired, relationship.fromTableName)
                });
                valid = false;
            } else {
                toTable = allTables.find((artifact) => artifact.realName === relationship.toTableName);
                valid = toTable && valid;
            }

            const columnInfos: IColumnRelationshipInformation[] = relationship.columnRelationshipInformations;
            for (const columnInfo of columnInfos) {
                if (fromTable && columnInfo.fromColumnName === '') {
                    violations.push({
                        context: owner,
                        message: () => sanitizeAndFormatString(shellStringResources.Validator_Relationship_ColumnNameRequired, fromTable.realName)
                    });
                    valid = false;
                }
                if (toTable && columnInfo.toColumnName === '') {
                    violations.push({
                        context: owner,
                        message: () => sanitizeAndFormatString(shellStringResources.Validator_Relationship_ColumnNameRequired, toTable.realName)
                    });
                    valid = false;
                }
            }

            if (valid) {
                const allRelationships = await ShellAPI.artifacts.listArtifacts('relationship');
                const selectedRelationships = allRelationships.filter((artifact: Relationship) =>
                    artifact.parentInfo && artifact.parentInfo.resourceName === named.parentInfo.resourceName
                    && artifact.realName !== named.realName) as Relationship[];
                for (const selectedRelationship of selectedRelationships) {
                    if (RelationshipUtils.isSame(selectedRelationship, named)) {
                        violations.push({
                            context: owner,
                            message: () => sanitizeAndFormatString(shellStringResources.Validator_Relationship_AlreadyExist, selectedRelationship.content.fromTableName,
                                selectedRelationship.content.toTableName, ...RelationshipUtils.columnsToString(selectedRelationship.content.columnRelationshipInformations))
                        });
                    }
                }
                for (const columnInfo of columnInfos) {
                    if (TableUtils.getDataType(fromTable, columnInfo.fromColumnName) !== TableUtils.getDataType(toTable, columnInfo.toColumnName)) {
                        violations.push({
                            context: owner,
                            message: () => sanitizeAndFormatString(shellStringResources.Validator_Relationship_DatatypeMisMatch, columnInfo.fromColumnName,
                                fromTable.realName, columnInfo.toColumnName, toTable.realName) // TODO: refine the inaccurate single table reference for relationship.
                        });
                    }
                }
            }
            return violations;
        }
        return undefined;
    }
}
